export type GenerationQueryState = 'pending' | 'completed' | 'failed' | 'unknown'

export interface NormalizedGenerationQuery {
  message: string
  state: GenerationQueryState
  urls: string[]
  credit?: number
}

export interface ActiveGenerationTask {
  createdAt: string
  status: string
  taskId: string
  type: 'image' | 'video' | 'audio'
}

const PENDING_STATES = new Set([
  'accepted',
  'created',
  'generating',
  'in_progress',
  'pending',
  'processing',
  'queued',
  'queueing',
  'running',
  'submitted',
  'waiting',
])

const COMPLETED_STATES = new Set([
  'complete',
  'completed',
  'done',
  'finished',
  'succeeded',
  'success',
])

const FAILED_STATES = new Set([
  'canceled',
  'cancelled',
  'error',
  'failed',
  'failure',
  'timeout',
])

function asRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, any>
    : null
}

function candidatePayloads(response: unknown): Record<string, any>[] {
  const root = asRecord(response)
  if (!root) {
    return []
  }
  const candidates = [root]
  const firstData = asRecord(root.data)
  if (firstData) {
    candidates.unshift(firstData)
    const nestedData = asRecord(firstData.data)
    if (nestedData) {
      candidates.unshift(nestedData)
    }
  }
  const result = asRecord(root.result)
  if (result) {
    candidates.unshift(result)
  }
  return candidates
}

function pickResultUrl(record: Record<string, any>): string {
  const url = record.fileUrl
    ?? record.file_url
    ?? record.url
    ?? record.downloadUrl
    ?? record.download_url
    // 轮询接口按类型回传：image / video / audio
    ?? record.video
    ?? record.image
    ?? record.audio
    ?? record.videoUrl
    ?? record.imageUrl
    ?? record.audioUrl
    ?? record.video_url
    ?? record.image_url
    ?? record.audio_url
  return typeof url === 'string' && url ? url : ''
}

function extractUrlsFromValue(value: unknown): string[] {
  if (typeof value === 'string') {
    return value.startsWith('http') || value.startsWith('/') ? [value] : []
  }
  if (!Array.isArray(value)) {
    return []
  }
  return value.flatMap((item) => {
    if (typeof item === 'string') {
      return extractUrlsFromValue(item)
    }
    const record = asRecord(item)
    if (!record) {
      return []
    }
    const url = pickResultUrl(record)
    return url ? [url] : []
  })
}

export function extractGenerationTaskId(response: unknown): string {
  for (const payload of candidatePayloads(response)) {
    const value = payload.task_id ?? payload.taskId
    if (value !== undefined && value !== null && String(value)) {
      return String(value)
    }
  }
  return ''
}

export function normalizeActiveGenerationTasks(response: unknown): ActiveGenerationTask[] {
  const root = asRecord(response)
  const rawTasks = Array.isArray(root?.data)
    ? root.data
    : (Array.isArray(root?.data?.list) ? root.data.list : [])

  return rawTasks.flatMap((value: unknown) => {
    const task = asRecord(value)
    if (!task) {
      return []
    }
    const taskId = task.task_id ?? task.taskId
    const type = String(task.type ?? task.function_type ?? task.functionType ?? '').toLowerCase()
    if (!taskId || (type !== 'image' && type !== 'video' && type !== 'audio')) {
      return []
    }
    return [{
      createdAt: String(task.created_at ?? task.createdAt ?? ''),
      status: String(task.status ?? ''),
      taskId: String(taskId),
      type,
    } satisfies ActiveGenerationTask]
  }).sort((a, b) => {
    const bTime = Date.parse(b.createdAt) || 0
    const aTime = Date.parse(a.createdAt) || 0
    return bTime - aTime
  })
}

export function normalizeGenerationQuery(response: unknown): NormalizedGenerationQuery {
  const candidates = candidatePayloads(response)
  // 轮询接口成功时结果在 data 数组；WebSocket 完成时在 dataList。
  const urls = Array.from(new Set(candidates.flatMap(payload => [
    payload.dataList,
    payload.data_list,
    payload.data,
    payload.files,
    payload.outputs,
    payload.results,
  ].flatMap(extractUrlsFromValue))))

  const rawStatus = candidates
    .map(payload => payload.status ?? payload.task_status ?? payload.taskStatus ?? payload.state)
    .find(value => typeof value === 'string' && value.trim())
  const status = typeof rawStatus === 'string'
    ? rawStatus.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : ''
  const messageValue = candidates
    .map(payload => payload.errorMessage ?? payload.error_message ?? payload.message ?? payload.msg ?? payload.error)
    .find(value => typeof value === 'string' && value.trim())
  const message = typeof messageValue === 'string' ? messageValue : ''
  const creditValue = candidates
    .map(payload => payload.credit ?? payload.cost ?? payload.platformCredit)
    .map(value => Number(value))
    .find(value => Number.isFinite(value) && value > 0)
  const credit = creditValue && creditValue > 0 ? creditValue : undefined

  // 仅在拿到结果文件时视为完成，避免 success 但尚未带回 URL 时清空生成页。
  if (urls.length > 0) {
    return credit ? { message, state: 'completed', urls, credit } : { message, state: 'completed', urls }
  }
  if (FAILED_STATES.has(status)) {
    return credit ? { message, state: 'failed', urls: [], credit } : { message, state: 'failed', urls: [] }
  }
  if (PENDING_STATES.has(status) || COMPLETED_STATES.has(status)) {
    return credit ? { message, state: 'pending', urls: [], credit } : { message, state: 'pending', urls: [] }
  }
  return credit ? { message, state: 'unknown', urls: [], credit } : { message, state: 'unknown', urls: [] }
}
